import clsx from "clsx"
import L, { LatLngTuple } from "leaflet"
import { memo, useCallback } from "react"
import { Marker, MarkerProps, Tooltip } from "react-leaflet"
import { shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { selectOrderById } from "@/store/orders"
import { setAboutPanel, setShowDriverDetails } from "@/store/ui"
import { Order, RootState } from "@/types"

const TOOLTIP_OFFSET: [number, number] = [0, -12]

export const OrdersMarkers = ({
  selector,
  zIndexOffset,
}: {
  selector: (state: RootState) => Array<Order["id"]>
  zIndexOffset: number
}) => {
  const orders = useAppSelector(selector, shallowEqual)

  return (
    <>
      {orders.map((orderId) => (
        <div key={`order-marker-${orderId}`}>
          <PickupMarker orderId={orderId} zIndexOffset={zIndexOffset} />
          <DeliveryMarker orderId={orderId} zIndexOffset={zIndexOffset} />
        </div>
      ))}
    </>
  )
}

type OrderMarkerProps = {
  orderId: Order["id"]
  zIndexOffset: MarkerProps["zIndexOffset"]
}

const PickupMarker = memo(({ orderId, zIndexOffset }: OrderMarkerProps) => {
  const order = useAppSelector(selectOrderById(orderId), shallowEqual)
  const pickup = order?.pickup

  const dispatch = useDispatch()

  const showAboutOrder = useCallback(() => {
    dispatch(setAboutPanel({ type: "order", id: orderId }))
  }, [dispatch, orderId])

  const isFocused = useAppSelector(
    (state) =>
      state.ui.focusedOrderId === orderId ||
      state.ui.focusedOrderArea?.some((focusedOrder) => focusedOrder.id === orderId)
  )

  if (!order || !pickup) return null

  const icon = getMarkerIcon(order, "pickup", isFocused || false)
  const position: LatLngTuple = [pickup.lat, pickup.lng]

  if (position.some((coord) => !coord)) return null

  return (
    <Marker icon={icon} position={position} zIndexOffset={zIndexOffset} onClick={showAboutOrder}>
      <Tooltip className="custom-leaflet-tooltip text-center" direction="top" offset={TOOLTIP_OFFSET} opacity={1}>
        <div className="h100 text-gray-60">{pickup.name || "-"}</div>
        <div className="text-desc text-muted">{pickup.address}</div>
      </Tooltip>
    </Marker>
  )
})

const DeliveryMarker = memo(({ orderId, zIndexOffset }: OrderMarkerProps) => {
  const order = useAppSelector(selectOrderById(orderId), shallowEqual)
  const delivery = order?.delivery

  const dispatch = useDispatch()

  const showAboutOrder = useCallback(() => {
    if (!order) return

    dispatch(setAboutPanel({ type: "order", id: order.id }))
    if (order.driver_id) dispatch(setShowDriverDetails(order.driver_id))
  }, [dispatch, order])

  const isFocused = useAppSelector(
    (state) =>
      state.ui.focusedOrderId === orderId ||
      state.ui.focusedOrderArea?.some((focusedOrder) => focusedOrder.id === orderId)
  )

  if (!order || !delivery) return null

  const icon = getMarkerIcon(order, "delivery", isFocused || false)
  const position: LatLngTuple = [delivery.lat, delivery.lng]

  if (position.some((coord) => !coord)) return null

  return (
    <Marker icon={icon} position={position} zIndexOffset={zIndexOffset} onClick={showAboutOrder}>
      <Tooltip className="custom-leaflet-tooltip text-center" direction="top" offset={TOOLTIP_OFFSET} opacity={1}>
        <div className="h100 text-gray-60">Nadawca: {order.pickup?.name || "-"}</div>
        <div className="text-desc text-muted">{delivery.address}</div>
      </Tooltip>
    </Marker>
  )
})

function getMarkerIcon(order: Order, type: "pickup" | "delivery", isFocused: boolean) {
  const state = order[type]?.state

  return L.divIcon({
    className: clsx("order-marker", type, state, isFocused && "focused"),
    iconAnchor: [6, 6],
    popupAnchor: [0, -16],
    iconSize: [12, 12],
  })
}
